import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import JobDTO from '@src/adapters/dto/job/JobDTO';
import useFindJobsLazy from '@src/adapters/hooks/job/useFindJobs';
import DepartmentName from '@src/components/department/DepartmentName';
import Production from '@src/components/production/Production';
import ProductionCompany from '@src/components/productionCompany/ProductionCompany';
import { Routes } from '@src/navigation/Routes';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Props {
  productionId: string;
  newJob: JobDTO | undefined;
}

function List({ productionId, newJob }: Props) {
  const { execute: find } = useFindJobsLazy();
  const [jobs, setJobs] = useState<JobDTO[]>([]);
  const [recordCount, setRecordCount] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedPage, setSelectedPage] = useState(0);
  const navigate = useNavigate();

  const changePage = (value: number) => {
    setSelectedPage(value);
  };

  const changeRowsPerPage = (value: number) => {
    setRowsPerPage(value);
  };

  const findJobs = useCallback(() => {
    find({
      productionId: productionId,
      pageSize: rowsPerPage,
      currentPage: selectedPage,
    }).then((result) => {
      setJobs(result.jobs);
      setRecordCount(result.recordCount);
    });
  }, [find, rowsPerPage, selectedPage, productionId]);

  useEffect(() => {
    findJobs();
  }, [findJobs]);

  useEffect(() => {
    if (newJob) {
      setJobs((prev) => [...prev, newJob]);
    }
  }, [newJob]);

  const handleRowClick = (event: React.MouseEvent<unknown>, job: JobDTO) => {
    if (job.id) {
      navigate(Routes.Job, {
        state: { productionId: productionId, id: job.id },
      });
    }
  };

  return (
    <Paper
      elevation={8}
      sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <TableContainer>
        <Table
          aria-labelledby="tableTitle"
          size="small"
          aria-label="Contents table">
          <TableHead>
            <TableRow>
              <TableCell component="th" align="left">
                Job Number
              </TableCell>
              <TableCell component="th" align="left">
                Production Company
              </TableCell>
              <TableCell component="th" align="left">
                Production
              </TableCell>
              <TableCell component="th" align="left">
                Department
              </TableCell>
              <TableCell component="th" align="left">
                Conatact
              </TableCell>
              <TableCell component="th" align="left">
                Role
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job, index) => {
              return (
                <TableRow
                  hover
                  onClick={(event) => handleRowClick(event, job)}
                  role="checkbox"
                  tabIndex={-1}
                  key={index}
                  sx={{ cursor: 'pointer' }}>
                  <TableCell scope="row" align="center" sx={{ width: '120px' }}>
                    {job.jobId}
                  </TableCell>
                  <TableCell scope="row">
                    <ProductionCompany id={job.productionCompanyId} />
                  </TableCell>
                  <TableCell scope="row">
                    <Production id={job.productionId} />
                  </TableCell>
                  <TableCell scope="row">
                    <DepartmentName id={job.departmentId} />
                  </TableCell>
                  <TableCell scope="row">{job.contact.name}</TableCell>
                  <TableCell scope="row">{job.role.name}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={recordCount}
          rowsPerPage={rowsPerPage}
          page={selectedPage}
          onPageChange={(event, number) => changePage(number)}
          onRowsPerPageChange={(event) => {
            changeRowsPerPage(parseInt(event.target.value, 10));
            changePage(0);
          }}
        />
      </TableContainer>
    </Paper>
  );
}

export default List;
